import {FormSection} from '@/components/commons/form-section/FormSection'
import {DetailsField} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {formatLocaleDate} from '@/dayjs'
import {PlannedAccessesFieldsSchema} from '@/features/planned-accesses/formSchemas'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {assistiveRegimeToRehabCareCommitment, projectSectorToLabel, projectTypeToLabel} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const PlannedAccessesFormProjectSchema = PlannedAccessesFieldsSchema.pick({
    projectId: true
})
type PlannedAccessesFormProjectSchema = z.infer<typeof PlannedAccessesFormProjectSchema>

export const PlannedAccessesFormProjectSection = () => {
    const {t} = useTranslation()
    const {control} = useFormContext<PlannedAccessesFormProjectSchema>()
    const projectId = useWatch({control, name: 'projectId'})

    const {
        data: project,
        isLoading,
        isError
    } = useGetProject({
        urlParams: {
            projectId: projectId
        },
        params: {
            responseType: 'extended'
        },
        options: {
            enabled: !!projectId
        }
    })

    return (
        <FormSection title={t('projects:project_details')} hasBottomDivider>
            <Grid columns={3} gap={4}>
                {isLoading ? (
                    Array.from({length: 6}).map((_, index) => (
                        <DetailsField key={index}>
                            <Skeleton width="100%" height={20} />
                            <Skeleton width="80%" height={20} />
                        </DetailsField>
                    ))
                ) : isError ? (
                    <Text size="textSm" palette="danger" shade="700">
                        {t('errors:default')}
                    </Text>
                ) : project ? (
                    <>
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('commons:project_type')}
                            </Text>
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                {project.type ? t(projectTypeToLabel[project.type]) : '-'}
                            </Text>
                        </DetailsField>
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('commons:intervention_mode')}
                            </Text>
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                {t(projectSectorToLabel[project.sector])}
                            </Text>
                        </DetailsField>
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:rehab_care_commitment')}
                            </Text>
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                {project.assistiveRegime
                                    ? t('commons:duration_minutes', {
                                          value: assistiveRegimeToRehabCareCommitment[project.assistiveRegime]
                                      })
                                    : '-'}
                            </Text>
                        </DetailsField>
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:start_project_date')}
                            </Text>
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                {project.startDate ? formatLocaleDate(project.startDate, 'DD/MM/YYYY') : '-'}
                            </Text>
                        </DetailsField>
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('commons:expiration')}
                            </Text>
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                {project.expirationDate ? formatLocaleDate(project.expirationDate, 'DD/MM/YYYY') : '-'}
                            </Text>
                        </DetailsField>
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:project_days')}
                            </Text>
                            <Flexbox align="center" gap={1}>
                                <Text palette="neutral" shade="950" size="textSm" weight="500">
                                    {project.durationDays ?? '-'}{' '}
                                    {project.actualDays && (
                                        <Text palette="neutral" shade="500" size="textSm" render="span">
                                            ({t('projects:days_working_days', {count: project.actualDays})})
                                        </Text>
                                    )}
                                </Text>
                            </Flexbox>
                        </DetailsField>
                    </>
                ) : (
                    <Text size="textSm" palette="neutral" shade="400">
                        {t('commons:no_results')}
                    </Text>
                )}
            </Grid>
        </FormSection>
    )
}
